/**
 * Step-level Continue / Launch gating. Matches BLS-39 through BLS-48.
 * Frequency UI is validated locally but never blocks on missing API.
 */

import { BLS_MODE, FREQUENCY_PRESETS } from './constants.js';
import { getMetricById } from './metrics.js';
import { hasNonDisplayMedia, hasSizeMismatch } from './flights.js';

export function regularAudiencePercent(controlSplit) {
  const split = Number(controlSplit) || 0;
  return Math.max(0, 100 - split * 2);
}

export function isAudienceOverflow(controlSplit) {
  return Number(controlSplit) * 2 > 100;
}

export function validateFrequencyBuckets(frequencyTesting) {
  if (!frequencyTesting?.enabled) return { valid: true, buckets: [] };
  const buckets = (frequencyTesting.buckets || []).map(Number);
  if (!buckets.length) return { valid: false, message: 'Add at least one frequency bucket.' };
  if (buckets.some((value) => !Number.isFinite(value) || value < 1)) {
    return { valid: false, message: 'Buckets must be positive numbers.' };
  }
  for (let i = 1; i < buckets.length; i += 1) {
    if (buckets[i] <= buckets[i - 1]) {
      return { valid: false, message: 'Buckets must be in ascending order without duplicates.' };
    }
  }
  return { valid: true, buckets };
}

export function bucketsForPreset(presetId, currentBuckets) {
  const preset = FREQUENCY_PRESETS.find((item) => item.id === presetId);
  if (!preset || preset.id === 'custom') return currentBuckets?.length ? currentBuckets : [1, 3, 5];
  return [...preset.buckets];
}

export function canAdvanceStep(step, config, selectedSets) {
  if (step === 1) {
    if (!config.name?.trim()) return false;
    if (config.surveySource === 'upload') {
      return Boolean(config.htmlFile || config.htmlFileName) && Boolean(config.brandMetric);
    }
    if (config.surveySource !== 'build') return false;
    if (!config.brandMetric) return false;
    const metric = getMetricById(config.brandMetric);
    if (config.brandMetric === 'custom') {
      return Boolean(config.questionWording?.trim()) &&
        (config.answerOptions || []).filter((value) => String(value || '').trim()).length >= 2;
    }
    if (!metric) return false;
    if (!config.questionWording?.trim()) return false;
    return (config.answerOptions || []).filter((value) => String(value || '').trim()).length >= 2;
  }

  if (step === 2) {
    if (isAudienceOverflow(config.controlSplit)) return false;
    const split = Number(config.controlSplit);
    if (!Number.isFinite(split) || split < 1 || split > 50) return false;
    if (!(Number(config.exposureFrequency) >= 1)) return false;
    if (!(Number(config.askLimit) >= 1)) return false;
    if (!(Number(config.returnFrequency) >= 1)) return false;
    if (!(Number(config.displayWindowMin) >= 1) || !(Number(config.displayWindowMax) >= 1)) return false;
    return true;
  }

  if (step === 3) {
    if (!config.blsMode) return false;
    if (config.blsMode === BLS_MODE.OPTIMIZED) {
      if (!config.selectedSetIds?.length) return false;
      if (hasNonDisplayMedia(selectedSets)) return false;
      if (hasSizeMismatch(selectedSets)) return false;
      const freq = validateFrequencyBuckets(config.frequencyTesting);
      if (!freq.valid) return false;
      const lead = Number(config.minPerformanceLead);
      if (!(lead >= 0.5) || lead > 50) return false;
      if (!(Number(config.minTestPeriod) >= 1)) return false;
      if (!(Number(config.minTestVolume) >= 1)) return false;
      if ((config.flightPromotion || 'always-on') !== 'full') {
        const allocation = Number(config.winningInventoryPercent);
        if (!(allocation >= 1) || allocation > 99) return false;
      }
    }
    return true;
  }

  return Boolean(config.blsMode && config.brandMetric);
}

export function launchIssues(config, selectedSets) {
  const issues = [];
  if (!canAdvanceStep(1, config, selectedSets)) issues.push('Survey question and answers are incomplete.');
  if (!canAdvanceStep(2, config, selectedSets)) issues.push('Audience & delivery configuration is invalid.');
  if (!canAdvanceStep(3, config, selectedSets)) issues.push('Measurement mode is incomplete or incompatible.');
  return issues;
}
